import React, { ReactNode } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { Flex } from '../Flex'
import { Text, TextVariant } from '../Text'
import { InputProps } from './types'

export const LabelSideText = styled(Text)`
  color: ${({ theme, color }) => (typeof color === 'string' ? color : theme.colors.white)};
  padding-left: 5px;
`

const textStyle = css`
  margin-top: 8px;
  width: 100%;
`

const StyledInputDescription = styled(Text)`
  ${textStyle}
  color: ${({ theme }) => theme.colors.gray6};
`

const StyledSuccessMessage = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))`
  ${textStyle}
  color: ${({ theme }) => theme.colors.green};
`

export const ErrorMessage = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))`
  color: ${({ theme }) => theme.colors.red};
  white-space: break-spaces;
  margin-top: 8px;
  width: 100%;
`

export const WarningMessage = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))`
  color: ${({ theme }) => theme.colors.yellow};
  white-space: break-spaces;
  margin-top: 8px;
  width: 100%;
`

export const InputTopDescription: React.FC<{ children?: ReactNode }> = ({ children, ...props }) => {
  return <StyledInputDescription {...props}>{children}</StyledInputDescription>
}

export const InputBottomDescription: React.FC<{ children?: ReactNode }> = ({ children, ...props }) => {
  return <StyledInputDescription {...props}>{children}</StyledInputDescription>
}

export const SuccessMessage: React.FC<{ children?: ReactNode }> = ({ children, ...props }) => {
  return <StyledSuccessMessage {...props}>{children}</StyledSuccessMessage>
}

export const RequiredStar: React.FC = () => {
  const theme = useTheme()
  return <span style={{ color: theme.colors.yellow, marginLeft: 8 }}>*</span>
}

export const InputLabel: React.FC<InputProps> = ({ label, labelBold, topDescription, required, labelSideText }) => {
  return (
    <Flex width="100%">
      {label && (
        <Flex alignItems="center" justifyContent="space-between" pb="5px" width="100%">
          <Flex alignItems="center">
            <Text variant={labelBold ? TextVariant.TEXT_XS_BOLD : TextVariant.TEXT_XS_MEDIUM}>
              {label} {required && <RequiredStar />}
            </Text>
          </Flex>
          {labelSideText && React.isValidElement(labelSideText) ? labelSideText : <Text pl="5px">{labelSideText}</Text>}
        </Flex>
      )}
      {topDescription && <InputTopDescription>{topDescription}</InputTopDescription>}
    </Flex>
  )
}

export const InputWrapper: React.FC<InputProps> = ({
  label,
  labelBold,
  labelSideText,
  topDescription,
  bottomDescription,
  error,
  errorMsg,
  success,
  successMsg,
  required,
  children,
  warning,
  warningMsg
}) => {
  return (
    <Flex flexDirection="column" position="relative" width="100%">
      <InputLabel
        label={label}
        labelBold={labelBold}
        labelSideText={labelSideText}
        topDescription={topDescription}
        required={required}
      />
      {children}
      {error && errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
      {warning && warningMsg && <WarningMessage>{warningMsg}</WarningMessage>}
      {success && successMsg && <SuccessMessage>{successMsg}</SuccessMessage>}
      {bottomDescription && <InputBottomDescription>{bottomDescription}</InputBottomDescription>}
    </Flex>
  )
}
